"use client";
import { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";

import validate from "helpers/form-validation";
import { Ga4WithEventParameters } from "helpers/google-tag-manager/events/ga4-with-eventparameters";
import { gtmLoadInterval } from "helpers/google-tag-manager/gtm-load-interval";

import { isMailAddressUsed, isPhoneNumberUsed, getContract, signup } from "services/auth";
import { getCities, getDistricts } from "services/location";

import Button from "components/common/button";
import ModalCorporateSignupSuccess from "components/modal-corporate-signup-success";
import SecondForm from "./second-form";
import FirstForm from "./first-form";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const CorporateForm = () => {
  const [currentStep, setCurrentStep] = useState("first");
  const [isFormSending, setIsFormSending] = useState(false);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [isOpenAgreements, setIsOpenAgreements] = useState(false);
  const [form, setForm] = useState({
    memberFullName: "",
    mobilePhone: null,
    emailAddress: null,
    companyName: "",
    cityId: null,
    districtId: null,
    contracts: null,
    formType: "corporate_registration@modal",
    allowCommunication: false,
    allowSms: false,
    application: "desktop-web",
    membershipContractApproved: true,
    type: "corporate",
  });

  const [errors, setErrors] = useState({
    memberFullName: {
      value: null,
      message: null,
    },
    mobilePhone: {
      value: null,
      message: null,
    },
    emailAddress: {
      value: null,
      message: null,
    },
    password: {
      value: null,
      message: null,
    },
    wrongUser: {
      value: null,
      message: null,
    },
    phoneConfirmationApproved: {
      value: null,
      message: null,
    },
    individualKvkk: {},
    membership: {},
    explicitConsent: {},
    companyName: {},
    cityId: {},
    districtId: {},
  });

  const recaptchaRef = useRef();

  const handleNextStepButton = async (event) => {
    event.preventDefault();
    const isValid = await firstFormValidate();
    if (!isValid) {
      return null;
    }
    setCurrentStep("second");
  };

  const handleBackButton = (event) => {
    event.preventDefault();
    setCurrentStep("first");
  };

  const handleRegisterButton = async (event) => {
    event.preventDefault();
    setIsFormSending(true);
    const isValid = await secondFormValidate();
    if (!isValid) {
      setIsFormSending(false);
      return null;
    }

    const gRecaptchaResponse = await recaptchaRef.current.executeAsync();
    const sendData = {
      ...form,
      emailAddress: form.emailAddress.toLowerCase(),
      gRecaptchaResponse,
    };

    const postSignupService = await signup(sendData, "corporate");
    if (postSignupService.status === "success") {
      Ga4WithEventParameters({
        eventName: "register_corporate",
        eventParameters: { method: "email", user_id: postSignupService.result?.leadId },
      });
      setSuccessModalOpen(true);
    }
  };

  const handleInputFocus = (type) => {
    setErrors({ ...errors, [type]: { value: false, message: null }, wrongUser: { value: false, message: null } });
  };

  const firstFormValidate = async () => {
    const fieldErrors = await validate({
      name: form.memberFullName === "" ? null : form.memberFullName,
      phone: form.mobilePhone === "" ? null : form.mobilePhone,
      email: form.emailAddress === "" ? null : form.emailAddress,
    });

    const errorName = fieldErrors.name;
    let errorEmail = fieldErrors.email;
    let errorMobilePhone = fieldErrors.phone;
    const errorPhoneConfirmation = fieldErrors.phoneConfirmation;

    if (!errorEmail) {
      const params = {
        emailAddress: form.emailAddress,
      };
      await isMailAddressUsed(params).then((response) => {
        if (!response.result?.valid) {
          errorEmail = "*Bu eposta adresi daha önce kullanılmış";
        }
      });
    }

    if (form.mobilePhone && form.mobilePhone.length > 0) {
      if (form.mobilePhone.length !== 10) {
        errorMobilePhone = "Geçerli bir telefon numarası girilmelidir.";
      } else {
        const params = {
          phoneNumber: form.mobilePhone,
          accountType: "CORPORATE",
        };
        await isPhoneNumberUsed(params).then((res) => {
          if (!res.result?.valid) {
            errorMobilePhone = "*Bu telefon numarası daha önce kullanılmış";
          }
        });
      }
    }

    if (errorName || errorEmail || errorMobilePhone || errorPhoneConfirmation) {
      setErrors({
        memberFullName: {
          value: !!errorName,
          message: errorName,
        },
        emailAddress: {
          value: !!errorEmail,
          message: errorEmail,
        },
        mobilePhone: {
          value: !!errorMobilePhone,
          message: errorMobilePhone,
        },
        phoneConfirmationApproved: {
          value: errorPhoneConfirmation,
          message: "*Doğrulama yapılmalıdır",
        },
      });
      return false;
    }

    return true;
  };

  const secondFormValidate = async () => {
    const fieldErrors = await validate({
      companyName: form.companyName === "" ? null : form.companyName,
      cityId: form.cityId === "" ? null : form.cityId,
      districtId: form.districtId === "" ? null : form.districtId,
    });
    const errorCompanyName = fieldErrors.companyName;
    const errorCityId = fieldErrors.cityId;
    const errorDistrictId = fieldErrors.districtId;
    const errorIndividualKvkk = !form.contracts?.find((contract) => contract.templateId === 4).approved;
    const errorMembership = !form.contracts?.find((contract) => contract.templateId === 2).approved;

    if (errorCompanyName || errorCityId || errorDistrictId || errorIndividualKvkk || errorMembership) {
      setErrors({
        ...errors,
        companyName: {
          value: !!errorCompanyName,
          message: errorCompanyName,
        },
        cityId: {
          value: !!errorCityId,
          message: errorCityId,
        },
        districtId: {
          value: !!errorDistrictId,
          message: errorDistrictId,
        },
        individualKvkk: {
          value: errorIndividualKvkk,
          message: errorIndividualKvkk,
        },
        membership: {
          value: errorMembership,
          message: errorMembership,
        },
      });
      return false;
    }
    return true;
  };

  const getContractContent = async () => {
    try {
      const response = await getContract(form.formType);
      if (response.status !== "success") return;

      const { result } = response;
      let contracts = [];
      const hasResult = result?.length > 0;

      if (hasResult) {
        contracts = result.reduce((acc, contractItem) => {
          const { templateId, version } = contractItem;
          acc.push({
            templateId,
            version,
            approved: false,
          });
          return acc;
        }, []);
      }
      setForm({ ...form, contracts });
    } catch (error) {
      console.error({ error });
    }
  };

  const prepareCities = async () => {
    try {
      const arr = [];
      const res = await getCities();
      await res?.result?.map((item) => arr.push({ key: item.id, value: item.name }));
      setCities(arr);
    } catch (error) {
      console.error("Error from prepareCities: ", error);
    }
  };

  const fillDistricts = async () => {
    try {
      const arr = [];
      if (!form.cityId) {
        return;
      }
      const res = await getDistricts(form.cityId);
      await res?.result?.map((item) => arr.push({ key: item.id, value: item.name, ...item }));
      setDistricts(arr);
    } catch (error) {
      console.error("Error from getDistricts: ", error);
    }
  };

  const handleSuccessModalClose = () => {
    recaptchaRef.current.reset();
    setSuccessModalOpen(false);
    setIsFormSending(false);
  };

  useEffect(() => {
    getContractContent();
    prepareCities();
    gtmLoadInterval(() => {
      Ga4WithEventParameters({ eventName: "form", eventParameters: { label: "view||register-corporate-form" } });
    });
  }, []);

  useEffect(() => {
    if (form.cityId !== null) {
      fillDistricts();
    }
  }, [form.cityId]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.formWrapper}>
        <form>
          {currentStep === "first" ? (
            <FirstForm errors={errors} form={form} setForm={setForm} handleInputFocus={handleInputFocus} />
          ) : (
            <SecondForm
              errors={errors}
              setErrors={setErrors}
              form={form}
              setForm={setForm}
              recaptchaRef={recaptchaRef}
              cities={cities}
              districts={districts}
              handleInputFocus={handleInputFocus}
              isOpenAgreements={isOpenAgreements}
              setIsOpenAgreements={setIsOpenAgreements}
            />
          )}
          <div className={styles.loginButton}>
            {currentStep === "first" ? (
              <Button size="M" onClick={(e) => handleNextStepButton(e)}>
                Devam Et
              </Button>
            ) : (
              <Button size="M" type="submit" onClick={(e) => handleRegisterButton(e)} isLoading={isFormSending}>
                Üye Ol
              </Button>
            )}
          </div>
          {currentStep === "first" ? (
            <div className={styles.signUp}>
              <p>Zaten üye misin?</p>
              <span>
                <a href="/giris/" className={styles.forgetPasswordLink}>
                  Giriş Yap
                </a>
              </span>
            </div>
          ) : (
            <button className={styles.backButton} onClick={(e) => handleBackButton(e)}>
              Geri
            </button>
          )}
        </form>
        <Modal isOpen={successModalOpen} handleClose={() => handleSuccessModalClose()}>
          <ModalCorporateSignupSuccess />
        </Modal>
      </div>
    </div>
  );
};

export default CorporateForm;
